import React, { useState, useCallback } from "react";
import { Col, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import ProductItem from "../components/ProductItem";
import ProductCardSkeleton from "../components/skeletons/ProductCard";
import {
  ArrowLeft, Clock, GeoAlt, StarFill, Star,
  CheckCircleFill, ArrowClockwise, ExclamationCircleFill, BoxSeam,
  Heart, HeartFill, ShareFill, HandThumbsUpFill, Shop,
} from "react-bootstrap-icons";
import { motion, AnimatePresence } from "framer-motion";
import {
  useStoreInfo, useStoreCatalog, useStoreRatingStats, useUserStoreRating,
} from "../lib/query/queries/useStoreQueries";
import { useSubmitStoreRating } from "../lib/query/mutations/useStoreMutations";
import { toast } from "../utilities/toast";
import { useUserContext } from "../context/UserContext";
import { useServiceAvailability } from "../context/ServiceAvailabilityContext";
import { useCachedImageSrc } from "../hooks/useCachedImageSrc";
import { PullToRefreshWrapper } from "../components/pullRefresh";
import { Seo } from "../components/Seo";
import { Breadcrumb } from "../components/Breadcrumb";
import { generateStoreSEO, generateNoIndexSEO } from "../lib/seo/metaGenerators";
import { isLatLngCoordinates, toPlusCode } from "../utilities/format";

// ─── Skeletons ────────────────────────────────────────────────────────────────

function StoreNameSkeleton() {
  const pulse = { backgroundColor: "rgba(118,118,128,0.12)", borderRadius: 6 };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ ...pulse, width: 180, height: 24 }} />
      <div style={{ ...pulse, width: 90, height: 18, borderRadius: 10 }} />
    </div>
  );
}

function ShowSkeleton() {
  return (
    <Row xs={1} sm={2} lg={3} className="g-3 g-md-4">
      {[...Array(6)].map((_, i) => (
        <Col key={i}><ProductCardSkeleton /></Col>
      ))}
    </Row>
  );
}

// ─── RatingCard ───────────────────────────────────────────────────────────────

const RatingCard = ({ name, storeId }) => {
  const { user, token } = useUserContext();
  const navigate = useNavigate();
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingError, setRatingError] = useState(null);

  const { data: userRating, isLoading: isRatingLoading } = useUserStoreRating(
    storeId,
    user?.uid
  );
  const rating = userRating?.rating || 0;

  // Invalidates both the store's rating stats (average/count shown in the
  // header chip) and this user's own rating — previously submitStoreRating
  // only updated local state, leaving the average/count stale until a full
  // page reload.
  const { mutate: rate, isPending: isSubmitting } = useSubmitStoreRating({
    onSuccess: () => toast.success("Your rating has been recorded", 2000),
    onError: (err) =>
      setRatingError(err.message || "Failed to submit rating. Please try again."),
  });

  const handleRating = useCallback((value) => {
    if (!user) {
      toast.error("Please login to rate this store");
      navigate("/login");
      return;
    }
    setRatingError(null);
    rate({ storeId, rating: value, uid: user.uid, token });
  }, [storeId, user, token, navigate, rate]);

  const starsDisabled = isSubmitting || !user || isRatingLoading;
  const displayRating = hoverRating || rating;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        backgroundColor: "#fff",
        borderRadius: 14,
        border: "1px solid rgba(15,23,42,0.07)",
        padding: "12px 16px",
        boxShadow: "0 1px 6px rgba(15,23,42,0.05)",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      {/* Label column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1c1c1e" }}>
          {name ? `Rate ${name}` : "Rate this store"}
        </span>
        <div style={{ marginTop: 2 }}>
          {!user ? (
            <span style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.45)" }}>
              Sign in to leave a rating
            </span>
          ) : rating > 0 ? (
            <span style={{ fontSize: "0.75rem", color: "#34c759", fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}>
              <CheckCircleFill size={10} color="#34c759" />
              {rating} {rating === 1 ? "star" : "stars"} rated
            </span>
          ) : (
            <span style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.45)" }}>
              Tap a star to rate
            </span>
          )}
        </div>
      </div>

      {/* Stars column */}
      <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <motion.button
            key={star}
            whileHover={!starsDisabled ? { scale: 1.25 } : {}}
            whileTap={!starsDisabled ? { scale: 0.85 } : {}}
            transition={{ type: "spring", stiffness: 500, damping: 18 }}
            onClick={() => handleRating(star)}
            onMouseEnter={() => !starsDisabled && setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            disabled={starsDisabled}
            style={{
              background: "none", border: "none", padding: "4px 3px",
              cursor: starsDisabled ? "not-allowed" : "pointer",
              lineHeight: 1, display: "flex", alignItems: "center",
              opacity: starsDisabled && !isRatingLoading ? 0.35 : 1,
            }}
          >
            {displayRating >= star
              ? <StarFill size={22} color="#ff9500" style={{ filter: "drop-shadow(0 1px 3px rgba(255,149,0,0.3))" }} />
              : <Star size={22} color="rgba(60,60,67,0.18)" />
            }
          </motion.button>
        ))}
      </div>

      {/* Inline error */}
      {ratingError && (
        <div style={{
          position: "absolute", bottom: -34, left: 0, right: 0,
          fontSize: "0.75rem", color: "#ff3b30",
          display: "flex", alignItems: "center", gap: 5,
          padding: "0 4px",
        }}>
          <ExclamationCircleFill size={11} color="#ff3b30" />
          {ratingError}
        </div>
      )}
    </motion.div>
  );
};

// ─── StoreInfo ────────────────────────────────────────────────────────────────

const StoreInfo = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [bannerImgError, setBannerImgError] = useState(false);
  const [logoImgError, setLogoImgError] = useState(false);
  const { isOpen: isServiceOpen } = useServiceAvailability();

  // Each gets its own cache entry (and its own built-in retry, replacing the
  // manual retryCount/setTimeout backoff loop this component used to run),
  // while still driving one combined loading/error state for the page.
  const storeInfoQuery = useStoreInfo(storeId);
  const catalogQuery = useStoreCatalog(storeId);
  const ratingStatsQuery = useStoreRatingStats(storeId);

  const storeInfo = storeInfoQuery.data ?? {};
  const bannerSrc = storeInfo.bannerImgSrc || storeInfo.imgSrc;
  const cachedBannerSrc = useCachedImageSrc(bannerSrc);
  const cachedLogoSrc = useCachedImageSrc(storeInfo.imgSrc);
  const catalog = catalogQuery.data ?? [];
  const ratingStats = ratingStatsQuery.data ?? { average_rating: 0, total_no_ratings: 0 };
  const storeInfoLoading = storeInfoQuery.isLoading;
  const isCatalogLoading = catalogQuery.isLoading;
  const isError = storeInfoQuery.isError || catalogQuery.isError || ratingStatsQuery.isError;
  const error = isError ? "Failed to load store information. Please try again later." : null;

  const seo = !storeInfoLoading && !isError && storeInfo.store_name
    ? generateStoreSEO(storeId, storeInfo, ratingStats)
    : generateNoIndexSEO("Store");

  const fetchData = useCallback(() => {
    return Promise.all([
      storeInfoQuery.refetch(),
      catalogQuery.refetch(),
      ratingStatsQuery.refetch(),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const handleRetry = fetchData;

  const formatLocation = useCallback((loc) => {
    if (!loc) return "Location not available";

    if (isLatLngCoordinates(loc)) {
      const [latitude, longitude] = typeof loc === "string"
        ? loc.split(",").map((coord) => parseFloat(coord.trim()))
        : [loc.latitude, loc.longitude];
      return toPlusCode(latitude, longitude) || "Invalid location";
    }

    if (typeof loc === "string") return loc;
    return "Invalid location";
  }, []);

  const handleShare = useCallback(async () => {
    const shareData = {
      title: storeInfo.store_name || "Teco store",
      url: window.location.href,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // user cancelled the share sheet — no-op
      }
      return;
    }
    await navigator.clipboard.writeText(shareData.url);
    toast.success("Link copied to clipboard", 2000);
  }, [storeInfo.store_name]);

  return (
    <PullToRefreshWrapper onRefresh={fetchData}>
    <div style={{ minHeight: "100vh", padding: "24px 16px 80px" }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ maxWidth: 900, margin: "0 auto" }}
      >

        <Seo {...seo} />
        {!storeInfoLoading && !isError && <Breadcrumb items={seo.breadcrumbItems} />}

        {/* ── Error banner ─────────────────────────────────────────── */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              style={{
                backgroundColor: "rgba(255,59,48,0.08)",
                borderRadius: 14,
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ExclamationCircleFill size={15} color="#ff3b30" />
                <span style={{ fontSize: "0.875rem", color: "#ff3b30", fontWeight: 500 }}>{error}</span>
              </div>
              <button
                onClick={handleRetry}
                style={{
                  background: "none", border: "none", padding: "4px 0",
                  cursor: "pointer",
                  color: "#ff3b30", fontSize: "0.8125rem", fontWeight: 600,
                  display: "flex", alignItems: "center", gap: 4,
                }}
              >
                <ArrowClockwise size={13} /> Retry
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Store header ──────────────────────────────────────────── */}
        <div style={{ marginBottom: 20 }}>

          <button
            onClick={() => navigate(-1)}
            style={{
              width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
              backgroundColor: "rgba(60,60,67,0.08)", border: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", marginBottom: 12,
            }}
          >
            <ArrowLeft size={18} color="#1c1c1e" />
          </button>

          {/* Banner (outer wrapper has no overflow clipping, so the badge below can hang off the edge) */}
          <div style={{ position: "relative", width: "100%" }}>
          <div style={{
            position: "relative", width: "100%", aspectRatio: "16 / 9",
            borderRadius: 20, overflow: "hidden",
            backgroundColor: "var(--bs-body-bg)",
            boxShadow: "0 6px 20px rgba(15,23,42,0.08)",
          }}>
            {storeInfoLoading ? (
              <div style={{ width: "100%", height: "100%", backgroundColor: "rgba(118,118,128,0.12)" }} />
            ) : bannerSrc && !bannerImgError ? (
              <img
                src={cachedBannerSrc}
                alt={storeInfo.store_name}
                onError={() => setBannerImgError(true)}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div style={{
                width: "100%", height: "100%",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Shop size={40} color="rgba(60,60,67,0.25)" />
              </div>
            )}

            {/* Top overlay: action icons */}
            {!storeInfoLoading && (
              <div style={{
                position: "absolute", top: 18, left: 18, right: 18,
                display: "flex", alignItems: "flex-start", justifyContent: "flex-end", gap: 14,
              }}>
                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <button
                    onClick={() => setIsFavorite((v) => !v)}
                    aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                    style={{
                      width: 36, height: 36, borderRadius: "50%", border: "none",
                      backgroundColor: "rgba(255,255,255,0.92)",
                      boxShadow: "0 2px 8px rgba(15,23,42,0.15)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    {isFavorite
                      ? <HeartFill size={16} color="#ff3b30" />
                      : <Heart size={16} color="#1c1c1e" />}
                  </button>
                  <button
                    onClick={handleShare}
                    aria-label="Share store"
                    style={{
                      width: 36, height: 36, borderRadius: "50%", border: "none",
                      backgroundColor: "rgba(255,255,255,0.92)",
                      boxShadow: "0 2px 8px rgba(15,23,42,0.15)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <ShareFill size={14} color="#1c1c1e" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Merchant logo badge (overlaps bottom-left of banner; lives outside the
              overflow:hidden banner container above so it isn't clipped) */}
          {!storeInfoLoading && storeInfo.imgSrc && !logoImgError && (
            <div style={{
              position: "absolute", bottom: -30, left: 22,
              width: 70, height: 70, borderRadius: 18,
              backgroundColor: "#fff", padding: 9,
              boxShadow: "0 4px 14px rgba(15,23,42,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <img
                src={cachedLogoSrc}
                alt=""
                onError={() => setLogoImgError(true)}
                style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 10 }}
              />
            </div>
          )}
          </div>

          {/* Name + category */}
          <div style={{ marginTop: 54, paddingLeft: 2 }}>
            {storeInfoLoading ? (
              <StoreNameSkeleton />
            ) : (
              <>
                <h1 style={{
                  fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e",
                  letterSpacing: "-0.02em", margin: 0, lineHeight: 1.2,
                }}>
                  {storeInfo.store_name}
                </h1>
                {storeInfo.store_category && (
                  <span style={{
                    display: "inline-block", marginTop: 6,
                    fontSize: "0.75rem", fontWeight: 600,
                    color: "#007aff", backgroundColor: "rgba(0,122,255,0.1)",
                    borderRadius: 999, padding: "2px 10px",
                  }}>
                    {storeInfo.store_category}
                  </span>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10 }}>
                  {[
                    {
                      id: "rating",
                      icon: <HandThumbsUpFill size={12} color="#007aff" />,
                      label: `${(ratingStats.average_rating ?? 0).toFixed(1)} · ${ratingStats.total_no_ratings ?? 0} ratings`,
                      bg: "rgba(0,122,255,0.12)",
                      color: "#0062cc",
                    },
                    {
                      id: "hours",
                      // Reflects the same Teco-wide service_availability config
                      // that gates checkout (see ServiceAvailabilityContext /
                      // requireServiceOpen) — not this store's own hours.
                      icon: <Clock size={12} color={isServiceOpen ? "#b8860b" : "#FF9500"} />,
                      label: isServiceOpen ? "Open now" : "Closed",
                      bg: isServiceOpen ? "rgba(255,204,0,0.2)" : "rgba(255,149,0,0.12)",
                      color: isServiceOpen ? "#8a6d00" : "#FF9500",
                    },
                    {
                      id: "location",
                      icon: <GeoAlt size={12} color="rgba(60,60,67,0.7)" />,
                      label: formatLocation(storeInfo.location),
                      bg: "rgba(255,255,255,0.85)",
                      color: "rgba(60,60,67,0.75)",
                      maxWidth: 150,
                    },
                  ].map(({ id, icon, label, bg, color, maxWidth }) => (
                    <div
                      key={id}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        backgroundColor: bg,
                        boxShadow: "0 2px 8px rgba(15,23,42,0.12)",
                        borderRadius: 999, padding: "6px 14px",
                      }}
                    >
                      {icon}
                      <span style={{
                        fontSize: "0.8125rem", fontWeight: 500, color,
                        ...(maxWidth ? { maxWidth, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : {}),
                      }}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Rating card ───────────────────────────────────────────── */}
        <div style={{ marginBottom: 28 }}>
          <RatingCard name={storeInfo.store_name} storeId={storeId} />
        </div>

        {/* ── Catalog ───────────────────────────────────────────────── */}
        <AnimatePresence mode="wait">
          {isCatalogLoading ? (
            <motion.div key="skeleton" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ShowSkeleton />
            </motion.div>
          ) : catalog.length > 0 ? (
            <motion.div key="catalog" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Row xs={1} sm={2} lg={3} className="g-3 g-md-4">
                {catalog.map((product) => (
                  <Col key={product.id}><ProductItem {...product} /></Col>
                ))}
              </Row>
            </motion.div>
          ) : (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div style={{ textAlign: "center", padding: "48px 24px" }}>
                <div style={{
                  width: 68, height: 68, borderRadius: "50%",
                  backgroundColor: "rgba(0,122,255,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 18px",
                }}>
                  <BoxSeam size={30} color="#007aff" />
                </div>
                <h3 style={{
                  fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e",
                  letterSpacing: "-0.01em", marginBottom: 6,
                }}>
                  No products available
                </h3>
                <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", margin: 0 }}>
                  Check back later for updates
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </motion.div>
    </div>
    </PullToRefreshWrapper>
  );
};

export default StoreInfo;
